"use client";

import { publicEnv } from "@/lib/public-env";

type Boundary = "root" | "route" | "glyph";

const reported = new WeakMap<object, Set<string>>();
const TYPES = new Set([
  "Error",
  "TypeError",
  "ReferenceError",
  "RangeError",
  "SyntaxError",
  "SecurityError",
  "QuotaExceededError",
  "ChunkLoadError",
]);

function isBrowserError(error: unknown): error is Error | DOMException {
  return (
    error instanceof Error ||
    (typeof DOMException !== "undefined" && error instanceof DOMException)
  );
}

/** Only locations of observed first-party Next chunks; no messages, query or function names. */
function firstPartyFrames(error: Error | DOMException): string | undefined {
  if (typeof window === "undefined") return undefined;
  const stack: unknown = Reflect.get(error, "stack");
  if (typeof stack !== "string") return undefined;
  const known = new Set<string>();
  let resources: string[] = [];
  try {
    if (typeof performance.getEntriesByType === "function")
      resources = performance
        .getEntriesByType("resource")
        .slice(-256)
        .map((entry) => entry.name);
  } catch {
    // Resource timing can be restricted; script elements still identify public chunks.
  }
  for (const source of [
    ...Array.from(document.scripts, (script) => script.src).slice(-64),
    ...resources,
  ]) {
    try {
      const url = new URL(source, window.location.origin);
      if (
        url.origin === window.location.origin &&
        /^\/_next\/static\/[A-Za-z0-9_./%()[\]-]+\.js$/.test(url.pathname)
      )
        known.add(url.pathname);
    } catch {
      /* Ignore unknown resource formats. */
    }
  }
  const frames: string[] = [];
  for (const match of stack
    .slice(0, 16_384)
    .matchAll(/(https?:\/\/[^\s)]+):(\d{1,8}):(\d{1,8})/g)) {
    try {
      const url = new URL(match[1] ?? "");
      if (
        url.origin !== window.location.origin ||
        !known.has(url.pathname) ||
        url.pathname.length > 200
      )
        continue;
      const frame = `${url.pathname}:${match[2]}:${match[3]}`;
      if (!frames.includes(frame)) frames.push(frame);
      if (frames.length === 3) break;
    } catch {
      /* Ignore unknown stack formats. */
    }
  }
  return frames.length ? frames.join("\n") : undefined;
}

/** Bounded classifications only: wallet/provider messages and causes can contain credentials. */
export function boundaryErrorContext(error: unknown, boundary: Boundary) {
  const path = typeof window === "undefined" ? "" : window.location.pathname;
  const segment = path.split("/")[1] ?? "";
  const route = [
    "",
    "claim",
    "rip",
    "inventory",
    "game",
    "cards",
    "rules",
    "how-to-play",
  ].includes(segment)
    ? `/${segment}`
    : "/other";
  const context: Record<string, string> = {
    boundary,
    failure: "client_render",
    route,
    build: publicEnv("NEXT_PUBLIC_APP_VERSION") ?? "unknown",
  };
  if (!isBrowserError(error)) return context;
  context.error_type = TYPES.has(error.name) ? error.name : "Error";
  const react = /Minified React error #(\d{1,4})\b/.exec(error.message);
  if (react?.[1]) context.react_error = react[1];
  if (
    /ChunkLoadError|Loading (?:chunk|CSS chunk) .*failed/i.test(error.message)
  )
    context.failure = "chunk_load";
  else if (/SecurityError|QuotaExceededError/.test(error.name))
    context.failure = "browser_storage";
  const frames = firstPartyFrames(error);
  if (frames) context.frames = frames;
  const digest: unknown = Reflect.get(error, "digest");
  if (typeof digest === "string" && /^\d{1,20}$/.test(digest))
    context.digest = digest;
  return context;
}

/** Works even when the root provider tree failed before FaroInit could mount. */
export async function reportBoundaryError(error: unknown, boundary: Boundary) {
  try {
    if (typeof error === "object" && error !== null) {
      const seen = reported.get(error) ?? new Set<string>();
      if (seen.has(boundary)) return;
      seen.add(boundary);
      reported.set(error, seen);
    }
    const context = boundaryErrorContext(error, boundary);
    if (isBrowserError(error) && globalThis.crypto?.subtle) {
      try {
        const bytes = new TextEncoder().encode(
          `${error.name}\n${error.message}\n${String(Reflect.get(error, "stack") ?? "")}`.slice(
            0,
            16_384
          )
        );
        const hash = await crypto.subtle.digest("SHA-256", bytes);
        context.fingerprint = Array.from(new Uint8Array(hash), (byte) =>
          byte.toString(16).padStart(2, "0")
        ).join("");
      } catch {
        // A restricted browser can still report the safe classification.
      }
    }
    const { initFaro, rumError } = await import("@/lib/faro");
    initFaro();
    const safe = new Error(`Mudwater ${boundary} render failed`);
    safe.stack = `${safe.name}: ${safe.message}${context.frames ? `\n${context.frames}` : ""}`;
    rumError(safe, context);
  } catch {
    // Reporting is optional; neither telemetry nor an untrusted error may break recovery.
  }
}
